/**
 * ProgramProgressTable 메모 모달의 `record_class_text`(수업기록 메모) 전용.
 *
 * 같은 앱 안에서는 대부분 메모가 `<textarea>`로 구현되어 있어 Enter 가 곧 `\n` 이고,
 * React `value`/`onChange` 만으로 충분함 (예: 메모 모달의 학습내용·일반 메모·평가 메모,
 * 단원 팝업의 성취기준 textarea).
 *
 * 수업기록만 `#태그`를 칩(span)으로 보이게 하려면 contenteditable 이 필요하고,
 * 그때는 (1) 줄바꿈을 BR+빈 TextNode 로 정규화(문서 끝은 appendChild) (2) 칩 개수 맞출 때 DOM 재구성
 * (3) controlled `value` 와 편집 중 DOM 경합 방지(flushSync·포커스 중 fill 생략) 등
 * textarea 대비 추가 로직이 들어간다.
 */
import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { resolveStorageTagRaw } from '../../services/memoTagSealService';
import type { ClassMemberRow } from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  countChippableTagsInNote,
  formatClassRecordTagDisplay,
  isChippableClosedTag,
  isMemberTagBody,
  isSealedTagBody,
  isTagMatchRenderedAsChip,
  listClosedChippableTagBodiesInNote,
  type ClassRecordMemoTagRuleOptions,
} from '../../utils/classRecordMemoHashtags';

export const CLASS_RECORD_MEMO_CE_TAG = 'cp-memo-ce-tag';

const TAG_RE = /#[^\s#]+/g;
/** 편집기만: 빈 줄에 붙는 빈 TextNode 가 브라우저에서 지워지는 것을 막기 위함. 저장 문자열에서는 제거 */
const CE_EMPTY_LINE_PLACEHOLDER = '\u200b';

function stripCePlaceholder(s: string): string {
  return s.replace(/\u200b/g, '');
}

function domToClassRecordNote(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += stripCePlaceholder(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      out += '\n';
      return;
    }
    if (el.classList.contains(CLASS_RECORD_MEMO_CE_TAG)) {
      out += el.getAttribute('data-raw') ?? '';
      return;
    }
    el.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return out;
}

/** 칩 span 은 제외 — 엔터 시 태그 입력 여부 판별용 */
function domToPlainTypingNote(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += stripCePlaceholder(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      out += '\n';
      return;
    }
    if (el.classList.contains(CLASS_RECORD_MEMO_CE_TAG)) {
      return;
    }
    el.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return out;
}

function plainTextBeforeCaret(editor: HTMLElement): string {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return '';
  const r = sel.getRangeAt(0);
  if (!editor.contains(r.startContainer)) return '';
  const pre = document.createRange();
  try {
    pre.selectNodeContents(editor);
    pre.setEnd(r.startContainer, r.startOffset);
  } catch {
    return '';
  }
  const holder = document.createElement('div');
  holder.appendChild(pre.cloneContents());
  return domToPlainTypingNote(holder);
}

/** 직렬화 문자열 caretPos 직전까지의 "열린" #태그 본문(# 제외). 없으면 null */
function extractOpenTagBodyBeforeSerializedPos(note: string, caretPos: number): string | null {
  const head = note.slice(0, Math.max(0, caretPos));
  const m = head.match(/#[^\s#]+$/);
  return m ? m[0].slice(1) : null;
}

/** 칩 제외·커서 앞 plain 텍스트 기준(입력 중 #이름 태그 감지에 더 안정적) */
function extractOpenTagBodyBeforePlainCaret(editor: HTMLElement): string | null {
  const before = plainTextBeforeCaret(editor);
  const m = before.match(/#[^\s#]+$/);
  return m ? m[0].slice(1) : null;
}

function resolveOpenTagBodyAtCaret(editor: HTMLElement, note: string, caretPos: number): string | null {
  return (
    extractOpenTagBodyBeforeSerializedPos(note, caretPos) ??
    extractOpenTagBodyBeforePlainCaret(editor)
  );
}

/** 직렬화 note 끝(또는 caret)의 열린 `#body` 닫힘 위치 — 칩 직렬화와 plain caret 불일치 시 사용 */
function findOpenTagClosePosInNote(note: string, body: string, caretPos?: number): number | null {
  const b = (body ?? '').trim();
  if (!b) return null;
  const esc = b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const head =
    typeof caretPos === 'number' ? note.slice(0, Math.max(0, Math.min(caretPos, note.length))) : note;
  const m = head.match(new RegExp(`#${esc}$`));
  if (!m || m.index === undefined) return null;
  return m.index + m[0].length;
}

/** 닫는 공백만(줄바꿈 없음), 커서는 그 공백 직후 */
function noteCloseTagWithSpaceAtPos(note: string, pos: number): { next: string; caretOffset: number } {
  const clamped = Math.max(0, Math.min(pos, note.length));
  const next = note.slice(0, clamped) + ' ' + note.slice(clamped);
  return { next, caretOffset: clamped + 1 };
}

function noteInsertNewlineAtPos(note: string, pos: number): { next: string; caretOffset: number } {
  const clamped = Math.max(0, Math.min(pos, note.length));
  const next = note.slice(0, clamped) + '\n' + note.slice(clamped);
  return { next, caretOffset: clamped + 1 };
}

/**
 * 줄바꿈마다 BR을 넣고, 각 줄에 TextNode를 만든다(빈 줄도 빈 TextNode).
 * BR 직후 텍스트 노드가 없으면 브라우저가 입력을 윗줄 텍스트에 붙여 `\n`이 사라지고 칩이 윗줄 끝으로 붙는다.
 */
function appendPlainWithNewlines(container: HTMLElement, text: string) {
  if (text === '') return;
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    container.appendChild(
      document.createTextNode(line === '' ? CE_EMPTY_LINE_PLACEHOLDER : line)
    );
    if (i < lines.length - 1) {
      container.appendChild(document.createElement('br'));
    }
  }
}

function countChipElements(el: HTMLElement): number {
  return el.querySelectorAll(`span.${CLASS_RECORD_MEMO_CE_TAG}`).length;
}

function closestMemoChip(node: Node | null, editor: HTMLElement): HTMLElement | null {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as HTMLElement;
      if (el.classList.contains(CLASS_RECORD_MEMO_CE_TAG)) return el;
    }
    n = n.parentNode;
  }
  return null;
}

function isIgnorableEditorTextNode(node: Node): boolean {
  if (node.nodeType !== Node.TEXT_NODE) return false;
  return stripCePlaceholder(node.textContent ?? '').length === 0;
}

function previousLeafNode(node: Node, editor: HTMLElement): Node | null {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n.previousSibling) {
      n = n.previousSibling;
      while (n.lastChild) n = n.lastChild;
      return n;
    }
    n = n.parentNode;
  }
  return null;
}

function nextLeafNode(node: Node, editor: HTMLElement): Node | null {
  let n: Node | null = node;
  while (n && n !== editor) {
    if (n.nextSibling) {
      n = n.nextSibling;
      while (n.firstChild) n = n.firstChild;
      return n;
    }
    n = n.parentNode;
  }
  return null;
}

function skipIgnorableLeavesBackward(node: Node | null, editor: HTMLElement): Node | null {
  let n = node;
  while (n && isIgnorableEditorTextNode(n)) {
    n = previousLeafNode(n, editor);
  }
  return n;
}

function skipIgnorableLeavesForward(node: Node | null, editor: HTMLElement): Node | null {
  let n = node;
  while (n && isIgnorableEditorTextNode(n)) {
    n = nextLeafNode(n, editor);
  }
  return n;
}

/** 접힌 캐럿 바로 앞의 이름 태그 칩(Backspace 대상) */
function getChipBeforeCollapsedCaret(editor: HTMLElement): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;

  const inside = closestMemoChip(range.startContainer, editor);
  if (inside) return inside;

  const container = range.startContainer;
  const offset = range.startOffset;

  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent ?? '';
    if (stripCePlaceholder(text.slice(0, offset)).length > 0) return null;
    const prev = skipIgnorableLeavesBackward(previousLeafNode(container, editor), editor);
    return prev ? closestMemoChip(prev, editor) : null;
  }

  if (container.nodeType === Node.ELEMENT_NODE) {
    if (offset > 0) {
      let n: Node | null = container.childNodes[offset - 1] ?? null;
      if (!n) return null;
      while (n.lastChild) n = n.lastChild;
      n = skipIgnorableLeavesBackward(n, editor);
      return n ? closestMemoChip(n, editor) : null;
    }
    const prev = skipIgnorableLeavesBackward(previousLeafNode(container, editor), editor);
    return prev ? closestMemoChip(prev, editor) : null;
  }
  return null;
}

/** 접힌 캐럿 바로 뒤의 이름 태그 칩(Delete 대상) */
function getChipAfterCollapsedCaret(editor: HTMLElement): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;

  const inside = closestMemoChip(range.startContainer, editor);
  if (inside) return inside;

  const container = range.startContainer;
  const offset = range.startOffset;

  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent ?? '';
    if (stripCePlaceholder(text.slice(offset)).length > 0) return null;
    const next = skipIgnorableLeavesForward(nextLeafNode(container, editor), editor);
    return next ? closestMemoChip(next, editor) : null;
  }

  if (container.nodeType === Node.ELEMENT_NODE) {
    if (offset < container.childNodes.length) {
      let n: Node | null = container.childNodes[offset] ?? null;
      if (!n) return null;
      while (n.firstChild) n = n.firstChild;
      n = skipIgnorableLeavesForward(n, editor);
      return n ? closestMemoChip(n, editor) : null;
    }
    const next = skipIgnorableLeavesForward(nextLeafNode(container, editor), editor);
    return next ? closestMemoChip(next, editor) : null;
  }
  return null;
}

function serializedOffsetAtPoint(editor: HTMLElement, container: Node, offset: number): number {
  const pre = document.createRange();
  try {
    pre.setStart(editor, 0);
    pre.setEnd(container, offset);
  } catch {
    return domToClassRecordNote(editor).length;
  }
  const holder = document.createElement('div');
  holder.appendChild(pre.cloneContents());
  return domToClassRecordNote(holder).length;
}

function getSerializedSelectionRange(editor: HTMLElement): { start: number; end: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return null;
  const a = serializedOffsetAtPoint(editor, range.startContainer, range.startOffset);
  const b = serializedOffsetAtPoint(editor, range.endContainer, range.endOffset);
  return { start: Math.min(a, b), end: Math.max(a, b) };
}

function removeChipFromSerializedNote(
  note: string,
  caretPos: number,
  chipRaw: string,
  side: 'before' | 'after'
): { next: string; caretOffset: number } | null {
  const raw = chipRaw;
  if (!raw) return null;
  if (side === 'before') {
    // 칩 + 닫는 공백 단위
    if (
      caretPos >= raw.length + 1 &&
      note.slice(caretPos - raw.length - 1, caretPos) === `${raw} `
    ) {
      const start = caretPos - raw.length - 1;
      return { next: note.slice(0, start) + note.slice(caretPos), caretOffset: start };
    }
    if (caretPos >= raw.length && note.slice(caretPos - raw.length, caretPos) === raw) {
      const start = caretPos - raw.length;
      let end = caretPos;
      if (note[end] === ' ') end += 1;
      return { next: note.slice(0, start) + note.slice(end), caretOffset: start };
    }
    return null;
  }
  if (note.slice(caretPos, caretPos + raw.length + 1) === `${raw} `) {
    return {
      next: note.slice(0, caretPos) + note.slice(caretPos + raw.length + 1),
      caretOffset: caretPos,
    };
  }
  if (note.slice(caretPos, caretPos + raw.length) === raw) {
    let end = caretPos + raw.length;
    if (note[end] === ' ') end += 1;
    return {
      next: note.slice(0, caretPos) + note.slice(end),
      caretOffset: caretPos,
    };
  }
  return null;
}

/**
 * Backspace: 캐럿 앞의 닫힌 칩(`#tag `) 또는 깨진 안정 ID 태그(`#m:`/`#e:`)를 통째로 지움.
 * 닫는 공백만 지워 raw `#m:…`가 노출되는 것을 막는다.
 */
function findAtomicTagDeleteBefore(
  note: string,
  caretPos: number,
  members: ClassMemberRow[],
  tagRules?: ClassRecordMemoTagRuleOptions
): { next: string; caretOffset: number } | null {
  const head = note.slice(0, Math.max(0, caretPos));
  const closed = head.match(/#([^\s#]+) $/);
  if (closed && closed.index !== undefined) {
    const raw = `#${closed[1]}`;
    if (isChippableClosedTag(raw, members, tagRules)) {
      const start = closed.index;
      return { next: note.slice(0, start) + note.slice(caretPos), caretOffset: start };
    }
  }
  const open = head.match(/#([^\s#]+)$/);
  if (open && open.index !== undefined) {
    const body = open[1] ?? '';
    if (isMemberTagBody(body) || isSealedTagBody(body)) {
      const start = open.index;
      let end = caretPos;
      if (note[end] === ' ') end += 1;
      return { next: note.slice(0, start) + note.slice(end), caretOffset: start };
    }
  }
  return null;
}

/** Delete: 캐럿 뒤의 닫힌 칩·안정 ID 태그를 통째로 지움 */
function findAtomicTagDeleteAfter(
  note: string,
  caretPos: number,
  members: ClassMemberRow[],
  tagRules?: ClassRecordMemoTagRuleOptions
): { next: string; caretOffset: number } | null {
  const tail = note.slice(Math.max(0, caretPos));
  const closed = tail.match(/^#([^\s#]+) /);
  if (closed) {
    const raw = `#${closed[1]}`;
    if (isChippableClosedTag(raw, members, tagRules)) {
      const end = caretPos + closed[0].length;
      return { next: note.slice(0, caretPos) + note.slice(end), caretOffset: caretPos };
    }
  }
  const open = tail.match(/^#([^\s#]+)/);
  if (open) {
    const body = open[1] ?? '';
    if (isMemberTagBody(body) || isSealedTagBody(body)) {
      let end = caretPos + open[0].length;
      if (note[end] === ' ') end += 1;
      return { next: note.slice(0, caretPos) + note.slice(end), caretOffset: caretPos };
    }
  }
  return null;
}

function getCaretSerializedOffset(editor: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const r = sel.getRangeAt(0);
  if (!editor.contains(r.startContainer)) return domToClassRecordNote(editor).length;
  const pre = document.createRange();
  try {
    pre.setStart(editor, 0);
    pre.setEnd(r.startContainer, r.startOffset);
  } catch {
    return domToClassRecordNote(editor).length;
  }
  const holder = document.createElement('div');
  holder.appendChild(pre.cloneContents());
  return domToClassRecordNote(holder).length;
}

function setCaretSerializedOffset(editor: HTMLElement, offset: number): void {
  const sel = window.getSelection();
  if (!sel) return;
  let remaining = offset;

  const step = (parent: Node): boolean => {
    for (let i = 0; i < parent.childNodes.length; i += 1) {
      const node = parent.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent ?? '';
        const logicalLen = stripCePlaceholder(raw).length;
        if (logicalLen === 0) {
          if (remaining === 0) {
            const range = document.createRange();
            range.setStart(node, 0);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return true;
          }
          continue;
        }
        if (remaining <= logicalLen) {
          const range = document.createRange();
          range.setStart(node, remaining);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return true;
        }
        remaining -= logicalLen;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'BR') {
          if (remaining === 0) {
            const range = document.createRange();
            range.setStartBefore(el);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return true;
          }
          remaining -= 1;
        } else if (el.classList.contains(CLASS_RECORD_MEMO_CE_TAG)) {
          const rawLen = (el.getAttribute('data-raw') ?? '').length;
          if (remaining <= rawLen) {
            const range = document.createRange();
            if (remaining === 0) range.setStartBefore(el);
            else range.setStartAfter(el);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            return true;
          }
          remaining -= rawLen;
        } else if (step(node)) {
          return true;
        }
      }
    }
    return false;
  };

  if (!step(editor)) {
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

function fillClassRecordNoteEditor(
  el: HTMLElement,
  note: string,
  members: ClassMemberRow[],
  finalizeOpenTags: boolean,
  tagRules?: ClassRecordMemoTagRuleOptions
) {
  el.innerHTML = '';
  const re = new RegExp(TAG_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(note)) !== null) {
    const i = m.index;
    if (i > last) appendPlainWithNewlines(el, note.slice(last, i));
    const raw = m[0];
    const end = i + raw.length;
    const nextCh = end >= note.length ? '' : note[end]!;
    if (isTagMatchRenderedAsChip(raw, nextCh, members, { ...tagRules, finalizeOpenTags })) {
      const span = document.createElement('span');
      span.className = CLASS_RECORD_MEMO_CE_TAG;
      span.contentEditable = 'false';
      span.setAttribute('data-raw', raw);
      span.textContent = formatClassRecordTagDisplay(raw, members, tagRules);
      el.appendChild(span);
    } else {
      appendPlainWithNewlines(el, raw);
    }
    last = end;
  }
  if (last < note.length) appendPlainWithNewlines(el, note.slice(last));
}

function applySerializedNoteToEditor(
  el: HTMLElement,
  next: string,
  caretOffset: number,
  members: ClassMemberRow[],
  onChange: (note: string) => void,
  tagRules?: ClassRecordMemoTagRuleOptions
) {
  fillClassRecordNoteEditor(el, next, members, false, tagRules);
  const o = Math.min(Math.max(0, caretOffset), next.length);
  flushSync(() => onChange(next));
  el.focus({ preventScroll: true });
  setCaretSerializedOffset(el, o);
}

export interface ClassRecordMemoNoteEditorProps {
  value: string;
  onChange: (note: string) => void;
  members: ClassMemberRow[];
  /** 그룹 기준 기록 시 true(기본). 학급 전체 문맥에서만 false */
  applyBulkMemberTagRules?: boolean;
  /** `#e:…` 표시용 라벨 맵(세션) */
  sealedLabelByToken?: Record<string, string>;
  /**
   * 공백·Enter로 #태그가 닫히거나, blur 시 맨 끝 태그가 확정될 때 호출.
   * 수업기록 모드에서 멤버 전체 기준 그룹 자동 선택에 사용.
   * tagBody는 저장 규약 본문(`m:id` / `e:…` / 레거시).
   */
  onHashtagTagFinalized?: (tagBody: string) => void;
  placeholder?: string;
  className?: string;
}

export type ClassRecordMemoNoteEditorHandle = {
  focus: (options?: { preventScroll?: boolean }) => void;
  /** 확인·저장 직전: contenteditable DOM → 직렬화 문자열(에디터가 없으면 undefined) */
  readSerializedNoteFromDom?: () => string | undefined;
};

const ClassRecordMemoNoteEditor = forwardRef<ClassRecordMemoNoteEditorHandle, ClassRecordMemoNoteEditorProps>(
  function ClassRecordMemoNoteEditor(
    {
      value,
      onChange,
      members,
      applyBulkMemberTagRules = true,
      sealedLabelByToken,
      onHashtagTagFinalized,
      placeholder,
      className,
    },
    forwardedRef
  ) {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const composingRef = useRef(false);
    const closingTagRef = useRef(false);
    /** 직렬화 note에 새로 생긴 닫힌 `#태그 ` — input 경로(스페이스) 그룹 자동선택용 */
    const closedTagBodiesFingerprintRef = useRef('');
    const tagRules = useMemo<ClassRecordMemoTagRuleOptions>(
      () => ({ applyBulkMemberTagRules, sealedLabelByToken }),
      [applyBulkMemberTagRules, sealedLabelByToken]
    );
    const membersKey = useMemo(
      () => members.map((m) => `${m.id}:${displayClassMemberName(m)}`).join('|'),
      [members]
    );
    const prevMembersKeyRef = useRef(membersKey);

    useImperativeHandle(forwardedRef, () => ({
      focus: (options) => {
        const el = innerRef.current;
        if (!el) return;
        el.focus({ preventScroll: options?.preventScroll ?? true });
        const note = domToClassRecordNote(el);
        setCaretSerializedOffset(el, note.length);
      },
      readSerializedNoteFromDom: () => {
        const el = innerRef.current;
        if (!el) return undefined;
        return domToClassRecordNote(el);
      },
    }));

    const maybeNotifyTagFinalized = useCallback(
      (tagBody: string) => {
        const body = (tagBody ?? '').trim();
        if (!body || body === '전체' || (body.endsWith('-') && body.length > 1)) return;
        onHashtagTagFinalized?.(body);
      },
      [onHashtagTagFinalized]
    );

    const fingerprintClosedTagBodies = useCallback(
      (note: string) => listClosedChippableTagBodiesInNote(note, members, tagRules).join('\0'),
      [members, tagRules]
    );

    const notifyNewlyClosedTagsInNote = useCallback(
      (note: string) => {
        const bodies = listClosedChippableTagBodiesInNote(note, members, tagRules);
        const fp = bodies.join('\0');
        const prevFp = closedTagBodiesFingerprintRef.current;
        if (fp === prevFp) return;
        const prevSet = new Set(prevFp ? prevFp.split('\0').filter(Boolean) : []);
        for (const body of bodies) {
          if (!prevSet.has(body)) maybeNotifyTagFinalized(body);
        }
        closedTagBodiesFingerprintRef.current = fp;
      },
      [members, maybeNotifyTagFinalized, tagRules]
    );

    const closeOpenTagWithSpace = useCallback(
      async (el: HTMLElement, openBody: string) => {
        if (closingTagRef.current) return;
        closingTagRef.current = true;
        try {
          const note = domToClassRecordNote(el);
          const pos = getCaretSerializedOffset(el);
          const closePos = findOpenTagClosePosInNote(note, openBody, pos) ?? pos;
          const tagStart = closePos - openBody.length - 1;
          let storageRaw = `#${openBody}`;
          try {
            storageRaw = await resolveStorageTagRaw(openBody, members);
          } catch (err) {
            console.error('해시태그 저장 정규화 실패:', err);
          }
          const storageBody = storageRaw.startsWith('#') ? storageRaw.slice(1) : storageRaw;
          const replaced =
            tagStart >= 0
              ? note.slice(0, tagStart) + storageRaw + note.slice(closePos)
              : note;
          const spacePos = tagStart >= 0 ? tagStart + storageRaw.length : closePos;
          const { next, caretOffset } = noteCloseTagWithSpaceAtPos(replaced, spacePos);
          applySerializedNoteToEditor(el, next, caretOffset, members, onChange, tagRules);
          closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(next);
          maybeNotifyTagFinalized(storageBody);
        } finally {
          closingTagRef.current = false;
        }
      },
      [fingerprintClosedTagBodies, members, maybeNotifyTagFinalized, onChange, tagRules]
    );

    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const cur = domToClassRecordNote(el);
      const focused = document.activeElement === el;

      if (focused) {
        if (cur === value) {
          if (prevMembersKeyRef.current !== membersKey && countChipElements(el) > 0) {
            const caret = getCaretSerializedOffset(el);
            fillClassRecordNoteEditor(el, value, members, true, tagRules);
            requestAnimationFrame(() => {
              el.focus({ preventScroll: true });
              setCaretSerializedOffset(el, Math.min(caret, value.length));
            });
          }
          prevMembersKeyRef.current = membersKey;
        }
        /* 포커스 중 cur !== value 이면 로컬 DOM 우선 — stale value 로 fill 하지 않음 */
        return;
      }

      if (cur !== value) {
        fillClassRecordNoteEditor(el, value, members, true, tagRules);
      }
      closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(value);
      prevMembersKeyRef.current = membersKey;
    }, [value, members, membersKey, tagRules, fingerprintClosedTagBodies]);

    const syncDomToParent = useCallback(() => {
        const el = innerRef.current;
        if (!el) return;
        const note = domToClassRecordNote(el);
        notifyNewlyClosedTagsInNote(note);
        onChange(note);
        const tok = countChippableTagsInNote(note, members, { finalizeOpenTags: false, ...tagRules });
        const chips = countChipElements(el);
        if (tok !== chips) {
          const caret = getCaretSerializedOffset(el);
          fillClassRecordNoteEditor(el, note, members, false, tagRules);
          requestAnimationFrame(() => {
            el.focus({ preventScroll: true });
            setCaretSerializedOffset(el, Math.min(caret, note.length));
          });
        }
    }, [notifyNewlyClosedTagsInNote, onChange, members, tagRules]);

    return (
      <div
        ref={innerRef}
        className={['cp-memo-ce-editor', className].filter(Boolean).join(' ')}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder ?? ''}
        tabIndex={0}
        onBeforeInput={(e) => {
          const ie = e.nativeEvent as InputEvent;
          if (ie.isComposing) return;
          const el = innerRef.current;
          if (!el || document.activeElement !== el) return;

          const deleteBackward =
            ie.inputType === 'deleteContentBackward' || ie.inputType === 'deleteWordBackward';
          const deleteForward =
            ie.inputType === 'deleteContentForward' || ie.inputType === 'deleteWordForward';

          if (deleteBackward || deleteForward) {
            const sel = window.getSelection();
            if (sel && !sel.isCollapsed) {
              const range = getSerializedSelectionRange(el);
              if (!range || range.start === range.end) return;
              e.preventDefault();
              const note = domToClassRecordNote(el);
              const next = note.slice(0, range.start) + note.slice(range.end);
              applySerializedNoteToEditor(el, next, range.start, members, onChange, tagRules);
              closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(next);
              return;
            }
            const note = domToClassRecordNote(el);
            const pos = getCaretSerializedOffset(el);
            const atomic = deleteBackward
              ? findAtomicTagDeleteBefore(note, pos, members, tagRules)
              : findAtomicTagDeleteAfter(note, pos, members, tagRules);
            if (atomic) {
              e.preventDefault();
              applySerializedNoteToEditor(
                el,
                atomic.next,
                atomic.caretOffset,
                members,
                onChange,
                tagRules
              );
              closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(atomic.next);
              return;
            }
            const chip = deleteBackward
              ? getChipBeforeCollapsedCaret(el)
              : getChipAfterCollapsedCaret(el);
            if (!chip) return;
            e.preventDefault();
            const raw = chip.getAttribute('data-raw') ?? '';
            const removed = removeChipFromSerializedNote(
              note,
              pos,
              raw,
              deleteBackward ? 'before' : 'after'
            );
            if (!removed) {
              chip.remove();
              syncDomToParent();
              return;
            }
            applySerializedNoteToEditor(
              el,
              removed.next,
              removed.caretOffset,
              members,
              onChange,
              tagRules
            );
            closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(removed.next);
            return;
          }

          if (ie.inputType !== 'insertText' || ie.data !== ' ') return;
          const note = domToClassRecordNote(el);
          const pos = getCaretSerializedOffset(el);
          const openBody = resolveOpenTagBodyAtCaret(el, note, pos);
          if (!openBody) return;
          e.preventDefault();
          closeOpenTagWithSpace(el, openBody);
        }}
        onKeyDown={(e) => {
          const native = e.nativeEvent as KeyboardEvent;
          if (native.isComposing || (native as unknown as { keyCode?: number }).keyCode === 229) return;

          const el = innerRef.current;
          if (!el || document.activeElement !== el) return;

          const isBackspace = e.key === 'Backspace';
          const isDelete = e.key === 'Delete';
          if (isBackspace || isDelete) {
            const sel = window.getSelection();
            if (sel && !sel.isCollapsed) {
              const range = getSerializedSelectionRange(el);
              if (!range || range.start === range.end) return;
              e.preventDefault();
              e.stopPropagation();
              native.stopImmediatePropagation?.();
              const note = domToClassRecordNote(el);
              const next = note.slice(0, range.start) + note.slice(range.end);
              applySerializedNoteToEditor(el, next, range.start, members, onChange, tagRules);
              closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(next);
              return;
            }
            const note = domToClassRecordNote(el);
            const pos = getCaretSerializedOffset(el);
            const atomic = isBackspace
              ? findAtomicTagDeleteBefore(note, pos, members, tagRules)
              : findAtomicTagDeleteAfter(note, pos, members, tagRules);
            if (atomic) {
              e.preventDefault();
              e.stopPropagation();
              native.stopImmediatePropagation?.();
              applySerializedNoteToEditor(
                el,
                atomic.next,
                atomic.caretOffset,
                members,
                onChange,
                tagRules
              );
              closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(atomic.next);
              return;
            }
            const chip = isBackspace
              ? getChipBeforeCollapsedCaret(el)
              : getChipAfterCollapsedCaret(el);
            if (!chip) return;
            e.preventDefault();
            e.stopPropagation();
            native.stopImmediatePropagation?.();
            const raw = chip.getAttribute('data-raw') ?? '';
            const removed = removeChipFromSerializedNote(
              note,
              pos,
              raw,
              isBackspace ? 'before' : 'after'
            );
            if (!removed) {
              chip.remove();
              syncDomToParent();
              return;
            }
            applySerializedNoteToEditor(
              el,
              removed.next,
              removed.caretOffset,
              members,
              onChange,
              tagRules
            );
            closedTagBodiesFingerprintRef.current = fingerprintClosedTagBodies(removed.next);
            return;
          }

          const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';
          const isEnter = e.key === 'Enter';
          if (!isSpace && !isEnter) return;

          const note = domToClassRecordNote(el);
          const pos = getCaretSerializedOffset(el);
          const openBody = resolveOpenTagBodyAtCaret(el, note, pos);

          if (isSpace) {
            if (!openBody) return;
            e.preventDefault();
            e.stopPropagation();
            native.stopImmediatePropagation?.();
            closeOpenTagWithSpace(el, openBody);
            return;
          }

          if (openBody) {
            e.preventDefault();
            e.stopPropagation();
            native.stopImmediatePropagation?.();
            closeOpenTagWithSpace(el, openBody);
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          native.stopImmediatePropagation?.();
          const { next, caretOffset } = noteInsertNewlineAtPos(note, pos);
          applySerializedNoteToEditor(el, next, caretOffset, members, onChange, tagRules);
        }}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={() => {
          composingRef.current = false;
          syncDomToParent();
        }}
        onInput={() => {
          if (composingRef.current) return;
          syncDomToParent();
        }}
        onPaste={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const el = innerRef.current;
          if (!el) return;
          const pasted = (e.clipboardData.getData('text/plain') ?? '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n');
          if (!pasted) return;
          const note = domToClassRecordNote(el);
          const pos = getCaretSerializedOffset(el);
          const next = note.slice(0, pos) + pasted + note.slice(pos);
          applySerializedNoteToEditor(el, next, pos + pasted.length, members, onChange, tagRules);
          notifyNewlyClosedTagsInNote(next);
        }}
        onBlur={() => {
          if (composingRef.current) return;
          const el = innerRef.current;
          if (!el) return;
          void (async () => {
            let note = domToClassRecordNote(el);
            const eofOpen = note.match(/#[^\s#]+$/);
            const eofBody = eofOpen ? eofOpen[0].slice(1) : '';
            if (eofBody) {
              try {
                const storageRaw = await resolveStorageTagRaw(eofBody, members);
                const tagStart = note.length - eofBody.length - 1;
                if (tagStart >= 0) {
                  note = note.slice(0, tagStart) + storageRaw;
                }
              } catch (err) {
                console.error('해시태그 blur 정규화 실패:', err);
              }
            }
            onChange(note);
            const tok = countChippableTagsInNote(note, members, { finalizeOpenTags: true, ...tagRules });
            if (tok !== countChipElements(el)) {
              fillClassRecordNoteEditor(el, note, members, true, tagRules);
            }
            if (eofBody) {
              const storageBody = note.match(/#[^\s#]+$/)?.[0]?.slice(1) ?? eofBody;
              maybeNotifyTagFinalized(storageBody);
            }
          })();
        }}
      />
    );
  }
);

export default ClassRecordMemoNoteEditor;
